import { useState, useEffect } from 'react';
import { FileText, Download, Loader2, CheckCircle, AlertCircle, Receipt } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function InvoiceTab({ token, membership, isActive }) {
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [regimenes, setRegimenes] = useState([]);
  const [usosCfdi, setUsosCfdi] = useState([]);
  const [formData, setFormData] = useState({
    rfc: '',
    razon_social: '',
    regimen_fiscal: '',
    codigo_postal: '',
    uso_cfdi: 'G03',
    email_facturacion: ''
  });

  useEffect(() => {
    fetchInvoices();
    fetchCatalogs();
  }, []);

  async function fetchInvoices() {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/invoice/my-invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setInvoices(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setIsLoading(false);
  }

  async function fetchCatalogs() {
    try {
      const [regRes, usosRes] = await Promise.all([
        fetch(`${API}/invoice/regimenes-fiscales`),
        fetch(`${API}/catalogo/usos-cfdi`)
      ]);
      if (regRes.ok) setRegimenes(await regRes.json());
      if (usosRes.ok) setUsosCfdi(await usosRes.json());
    } catch (error) {
      console.error('Error loading catalogs:', error);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!membership?.id) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/invoice/request`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          membership_id: membership.id,
          datos_fiscales: formData
        })
      });

      const data = await response.json();
      if (response.ok) {
        alert('Solicitud de factura enviada correctamente');
        setShowForm(false);
        fetchInvoices();
      } else {
        alert(data.detail || 'Error al solicitar factura');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error de conexión');
    }
    setIsSubmitting(false);
  }

  async function downloadInvoice(invoiceId) {
    try {
      const response = await fetch(`${API}/invoice/${invoiceId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura_${invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }

  const hasInvoice = invoices.some(inv => inv.membership_id === membership?.id && inv.status !== 'Cancelada');

  if (!isActive) {
    return (
      <div className="text-center py-12">
        <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400 mb-4">Requieres membresía vigente para solicitar facturas</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold">Facturación CFDI 4.0</h3>
          <p className="text-gray-400 text-sm">Solicita tu factura electrónica</p>
        </div>
        {!hasInvoice && !showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold">
            <FileText className="w-4 h-4 mr-2" />Solicitar Factura
          </Button>
        )}
      </div>

      {/* Invoice Request Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-[#111111] rounded-lg p-6 space-y-4">
          <h4 className="text-white font-medium mb-4">Datos Fiscales</h4>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400">RFC *</Label>
              <input
                type="text"
                value={formData.rfc}
                onChange={(e) => setFormData({...formData, rfc: e.target.value.toUpperCase()})}
                placeholder="XAXX010101000"
                className="w-full mt-1 p-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white"
                required
                maxLength={13}
              />
            </div>
            <div>
              <Label className="text-gray-400">Razón Social *</Label>
              <input
                type="text"
                value={formData.razon_social}
                onChange={(e) => setFormData({...formData, razon_social: e.target.value})}
                placeholder="Nombre o Razón Social"
                className="w-full mt-1 p-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400">Régimen Fiscal *</Label>
              <select
                value={formData.regimen_fiscal}
                onChange={(e) => setFormData({...formData, regimen_fiscal: e.target.value})}
                className="w-full mt-1 p-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white"
                required
              >
                <option value="">Seleccionar...</option>
                {regimenes.map(r => (
                  <option key={r.codigo} value={r.codigo}>{r.codigo} - {r.descripcion}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-gray-400">Código Postal *</Label>
              <input
                type="text"
                value={formData.codigo_postal}
                onChange={(e) => setFormData({...formData, codigo_postal: e.target.value})}
                placeholder="00000"
                className="w-full mt-1 p-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white"
                required
                maxLength={5}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400">Uso CFDI</Label>
              <select
                value={formData.uso_cfdi}
                onChange={(e) => setFormData({...formData, uso_cfdi: e.target.value})}
                className="w-full mt-1 p-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white"
              >
                {usosCfdi.map(u => (
                  <option key={u.codigo} value={u.codigo}>{u.codigo} - {u.descripcion}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-gray-400">Email para Factura</Label>
              <input
                type="email"
                value={formData.email_facturacion}
                onChange={(e) => setFormData({...formData, email_facturacion: e.target.value})}
                placeholder="facturacion@empresa.com"
                className="w-full mt-1 p-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={isSubmitting} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold">
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Enviar Solicitud
            </Button>
            <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-[#2a2a2a] text-gray-300">
              Cancelar
            </Button>
          </div>
        </form>
      )}

      {/* Invoices List */}
      {isLoading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No tienes facturas</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <InvoiceCard key={inv.id} invoice={inv} onDownload={downloadInvoice} />
          ))}
        </div>
      )}
    </div>
  );
}

function InvoiceCard({ invoice, onDownload }) {
  const statusColors = {
    'Pendiente': 'bg-yellow-500/20 text-yellow-400',
    'Generada': 'bg-green-500/20 text-green-400',
    'Timbrada': 'bg-blue-500/20 text-blue-400',
    'Cancelada': 'bg-red-500/20 text-red-400',
    'Error': 'bg-red-500/20 text-red-400'
  };

  const statusIcons = {
    'Pendiente': <Loader2 className="w-4 h-4" />,
    'Generada': <CheckCircle className="w-4 h-4" />,
    'Timbrada': <CheckCircle className="w-4 h-4" />,
    'Cancelada': <AlertCircle className="w-4 h-4" />,
    'Error': <AlertCircle className="w-4 h-4" />
  };

  return (
    <div className="flex items-center justify-between p-4 bg-[#111111] rounded-lg border border-[#2a2a2a]">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
          <FileText className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <p className="text-white font-medium">{invoice.folio}</p>
          <p className="text-gray-500 text-sm">{new Date(invoice.created_at).toLocaleDateString('es-MX')}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className="text-white font-semibold">${invoice.total?.toLocaleString()} MXN</p>
          <p className="text-gray-500 text-xs">{invoice.receptor_rfc}</p>
        </div>
        <Badge className={statusColors[invoice.status] || 'bg-gray-500/20 text-gray-400'}>
          <span className="flex items-center gap-1">
            {statusIcons[invoice.status]}
            {invoice.status}
          </span>
        </Badge>
        <Button onClick={() => onDownload(invoice.id)} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
          <Download className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default InvoiceTab;
